import { ErrorMessage } from "@hookform/error-message";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Checkbox,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Rating,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/nl";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React from "react";
import { Controller } from "react-hook-form";
import { regex } from "../../utils/regax";
import dayjs from "dayjs";
import theme from "../../styles/styleTheme";

type AdvancedInputProps = {
  disabled?: boolean;
  type: string;
  selectValues?: any[];
  label?: any;
  helperText?: string | React.ReactNode;
  control: any;
  name: any;
  rules?: any;
  errors?: any;
  changeValue?: (e: any) => void;
  customWidth?: string;
  customErrorMessage?: string;
  options?: any;
  row?: boolean;
  customValue?: any;
};

const AdvancedInput = ({
  type,
  selectValues,
  helperText,
  label,
  control,
  name,
  rules,
  changeValue,
  options,
  errors,
  customWidth,
  customErrorMessage,
  disabled,
  row,
  customValue
}: AdvancedInputProps) => {
  const validateRequired = (value: any) => {
    if (type === "select" || type === "select2") {
      // For select inputs, we only need to check if the value exists
      return value !== undefined && value !== null;
    }
    // For text inputs, check for empty strings and whitespace
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return true;
  };

  const combinedRules = {
    ...rules,
    validate: {
      ...rules?.validate,
      required: validateRequired
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <FormControl sx={{ width: "100%" }}>
          {(type === "text" || type === "number") && (
            <Controller
              name={name}
              control={control}
              rules={combinedRules}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  disabled={disabled}
                  label={label}
                  type={type === "number" ? "number" : "text"}
                  variant="outlined"
                  fullWidth
                  helperText={helperText}
                  required
                  onChange={(e: any) => {
                    if (changeValue != null) {
                      field.onChange(e);
                      changeValue(e);
                    }
                  }}
                  value={field.value}
                  error={!!error}
                  sx={{ width: "100%", maxWidth: "100%" }}
                />
              )}
            />
          )}
          {type === "stars" && (
            <Controller
              name={name}
              control={control}
              rules={rules}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <Rating
                  color="primary"
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                  size="large"
                  name="simple-controlled"
                  value={field.value}
                  onChange={(e: any) => {
                    if (changeValue != null) {
                      field.onChange(e);
                      changeValue(e);
                    }
                  }}
                />
              )}
            />
          )}

          {type === "multiline" && (
            <Controller
              name={name}
              control={control}
              rules={rules}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label={label}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={8}
                  sx={{ width: customWidth }}
                  helperText={helperText}
                  required
                  onChange={(e: any) => {
                    if (changeValue != null) {
                      field.onChange(e);
                      changeValue(e);
                    }
                  }}
                  value={field.value}
                  error={!!error}
                />
              )}
            />
          )}

          {type === "checkbox" && (
            <Controller
              name={name}
              rules={rules}
              control={control}
              render={({ field }) => {
                return (
                  <Box
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                    display={"flex"}
                    flexDirection={"row"}
                  >
                    <Checkbox
                      value={Boolean(field.value)}
                      checked={Boolean(field.value)}
                      onChange={(e: any) => {
                        field.onChange(e);
                        if (changeValue != null) {
                          changeValue(e);
                        }
                      }}
                    />
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                );
              }}
            />
          )}

          {type === "select" && (
            <Controller
              name={name}
              control={control}
              rules={combinedRules}
              render={({ field, fieldState: { error } }) => {
                return (
                  <>
                    <InputLabel>{label}</InputLabel>
                    <Select
                      label={label}
                      error={!!error}
                      onChange={(e: any) => {
                        field.onChange(e);
                        if (changeValue != null) {
                          changeValue(e);
                        }
                      }}
                      variant="outlined"
                      value={customValue ? customValue : field.value || 0}
                      required
                    >
                      {selectValues?.map((value) => (
                        <MenuItem key={value.id} value={value.id}>
                          {value.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                );
              }}
            />
          )}
          {type === "select2" && (
            <Controller
              name={name}
              control={control}
              rules={combinedRules}
              render={({ field, fieldState: { error } }) => {
                return (
                  <>
                    <InputLabel>{label}</InputLabel>
                    <Select
                      label={label}
                      error={!!error}
                      onChange={(e: any) => {
                        field.onChange(e);
                        if (changeValue != null) {
                          changeValue(e);
                        }
                      }}
                      variant="outlined"
                      value={customValue ? customValue : field.value || 0}
                      required
                    >
                      {selectValues?.map((value) => (
                        <MenuItem key={value.id} value={value.id}>
                          {value.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                );
              }}
            />
          )}

          {type === "hidden" && (
            <Controller
              name={name}
              control={control}
              rules={rules}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  sx={{ display: "none", width: 0, height: 0 }}
                  onChange={(e: any) => {
                    field.onChange(e);
                    if (changeValue != null) {
                      changeValue(e);
                    }
                  }}
                  value={field.value}
                  error={!!error}
                />
              )}
            />
          )}

          {type === "radio" && (
            <Controller
              name={name}
              control={control}
              rules={rules}
              render={({ field, fieldState: { error } }) => {
                return (
                  <RadioGroup
                    row={row}
                    value={customValue ? customValue : field.value || 2}
                    onChange={(e: any) => {
                      field.onChange(e.target.value);
                      if (changeValue != null) {
                        changeValue(e);
                      }
                    }}
                  >
                    {options?.map((option: any) => (
                      <FormControlLabel
                        key={option.id}
                        value={option.id}
                        control={<Radio />}
                        label={option.name}
                      />
                    ))}
                  </RadioGroup>
                );
              }}
            />
          )}
          {type === "date" && (
            <Controller
              name={name}
              control={control}
              rules={rules}
              render={({ field, fieldState: { error } }) => (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="nl"
                >
                  <DatePicker
                    value={dayjs(field.value)}
                    label={label}
                    slotProps={{
                      textField: {
                        helperText: helperText,
                        error: !!error,
                        InputLabelProps: {
                          shrink: true,
                        },
                      },
                    }}
                    sx={{ width: "100%" }}
                    onChange={(e: any) => {
                      const formattedDate = dayjs(e).format("YYYY-MM-DD");
                      field.onChange(formattedDate);
                      changeValue?.(formattedDate);
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          )}
        </FormControl>
      </Box>
      {errors && (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <>
              <Box
                color={"#B22"}
                bgcolor={"#fee"}
                border={"1px solid #fbb"}
                marginTop={"1rem"}
                padding={"0.5rem 1rem"}
                borderRadius={"0.5rem"}
                display={"flex"}
                alignContent={"center"}
              >
                <WarningAmberRoundedIcon sx={{ marginRight: 1 }} />
                <Typography>
                  {customErrorMessage ? (
                    <>{customErrorMessage}</>
                  ) : (
                    <>
                      {errors[name]?.type === "required" && `Veld is verplicht`}
                      {errors[name]?.type === "minLength" &&
                        `Veld moet groter zijn dan ${rules.minLength}`}
                      {errors[name]?.type === "min" &&
                        `Veld moet groter zijn dan  ${rules.min}`}
                      {errors[name]?.type === "pattern" &&
                        rules.pattern === regex.email &&
                        `Is geen geldig email adres`}
                      {errors[name]?.type === "pattern" &&
                        rules.pattern === regex.phone &&
                        `Is geen geldig telefoonnummer`}
                    </>
                  )}
                </Typography>
              </Box>
            </>
          )}
        />
      )}
    </>
  );
};

export default AdvancedInput;
